<script setup="">
import { debounce } from "lodash-es";
import { SearchIcon, XCircleIcon } from "lucide-vue-next";
import { computed, onMounted, ref } from "vue";

import { useTeam } from "@/composables/team";

const emit = defineEmits(["select"]);

const { team } = useTeam();

const searchText = ref("");
const page = ref(1);
const images = ref([]);

const hasPrev = computed(() => page.value > 1);
const hasNext = computed(() => page.value < 10);

const fetch = (page = 1) => {
  axios
    .get(route("app.ajax.assets.pixabay", [team.value]), {
      params: {
        page: page,
        per_page: 60,
        q: searchText.value,
        category: "backgrounds"
      }
    })
    .then(({ data }) => {
      images.value = data.hits;
    });
};

const prev = () => {
  if (page.value > 1) {
    page.value--;
    fetch(page.value);
  }
};

const next = () => {
  page.value++;
  fetch(page.value);
};

const select = (image) => {
  emit("select", image);
};

const search = debounce(() => {
  fetch(page.value);
}, 1000);

onMounted(() => {
  fetch();
});
</script>

<template>
  <div class="flex h-full flex-col space-y-4">
    <div class="h-8 flex-none">
      <div class="relative flex h-full">
        <input
          ref="inputRef"
          v-model="searchText"
          placeholder="검색어를 입력하세요"
          class="form-input h-full w-full rounded-full border-0 bg-gray-150 px-4 pl-9 text-xs+ text-800 hover:bg-gray-200 dark:bg-zinc-800 dark:placeholder-zinc-300 dark:ring-success/50 dark:hover:bg-gray-800 dark:focus:bg-gray-800"
          type="text"
          @keyup="search"
        />

        <div class="flex-center pointer-events-none absolute h-full w-10 text-600 peer-focus:text-primary dark:peer-focus:text-success">
          <SearchIcon class="h-4.5 w-4.5 transition-colors duration-200" />
        </div>

        <transition name="fade">
          <div
            v-if="searchText.length > 0"
            class="flex-center absolute right-0 h-full w-10 cursor-pointer text-600 hover:text-700 peer-focus:text-primary dark:peer-focus:text-success"
            @click="searchText = ''"
          >
            <XCircleIcon class="h-4.5 w-4.5" />
          </div>
        </transition>
      </div>
    </div>

    <div class="flex-1 overflow-y-auto border-y py-1 dark:border-zinc-700">
      <div class="grid grid-cols-3 gap-2">
        <div v-for="image in images" :key="image.id" class="relative cursor-pointer" @click="select(image)">
          <img :src="image.previewURL" alt="pixabay image" class="aspect-square h-full w-full object-cover" />
        </div>
      </div>
    </div>

    <div class="flex h-10 flex-none items-center justify-between">
      <div>
        <span class="text-sm text-700">페이지: {{ page }}/10</span>
        <p class="text-tiny text-600">Source: Pixabay</p>
      </div>

      <div class="flex space-x-1">
        <button v-if="hasPrev" class="rounded-md bg-gray-200 px-4 py-1.5 text-700 dark:bg-zinc-700" @click="prev">이전</button>
        <button v-if="hasNext" class="rounded-md bg-gray-200 px-4 py-1.5 text-700 dark:bg-zinc-700" @click="next">다음</button>
      </div>
    </div>
  </div>
</template>
